"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { prefersReducedMotion, useSeenOnce } from "./Reveal";

export type StatementSide = { title: string; paragraphs: string[] };

const SWAP_EVERY_MS = 4500;
/** Four swaps and back where it started: a demonstration, not a carousel. */
const MAX_SWAPS = 4;

/**
 * One real problem's statement in both languages (`EX-PUB19`). Once the card
 * is on screen it swaps a few times on its own and then rests on the page's
 * language; the visitor's own toggle stops it at once. Under reduced motion it
 * never swaps by itself.
 */
export function BilingualStatement({
  en,
  bn,
  locale,
}: {
  en: StatementSide;
  bn: StatementSide;
  locale: string;
}) {
  const t = useTranslations("judge.home");
  const ref = useRef<HTMLDivElement>(null);
  const seen = useSeenOnce(ref);
  const home: 0 | 1 = locale === "bn" ? 1 : 0;
  const [shown, setShown] = useState<0 | 1>(home);
  const [chosen, setChosen] = useState(false);

  useEffect(() => {
    if (!seen || chosen || prefersReducedMotion()) return;
    let swaps = 0;
    const timer = window.setInterval(() => {
      swaps += 1;
      setShown((side) => (side === 0 ? 1 : 0));
      if (swaps >= MAX_SWAPS) window.clearInterval(timer);
    }, SWAP_EVERY_MS);
    return () => window.clearInterval(timer);
  }, [seen, chosen]);

  function choose(side: 0 | 1) {
    setChosen(true);
    setShown(side);
  }

  const sides: ReadonlyArray<{ key: "en" | "bn"; label: string; side: StatementSide }> = [
    { key: "en", label: "English", side: en },
    { key: "bn", label: "বাংলা", side: bn },
  ];

  return (
    <div ref={ref}>
      <div
        className="relative mt-6 inline-grid grid-cols-2 rounded-full bg-neutral-100 p-[3px]"
        role="group"
        aria-label={t("bilingualToggle")}
      >
        <span
          className={cn(
            "absolute inset-y-[3px] left-[3px] w-[calc(50%-3px)] rounded-full bg-white shadow-sm transition-transform duration-200 ease-out",
            shown === 1 && "translate-x-full",
          )}
          aria-hidden
        />
        {sides.map((entry, index) => (
          <Button
            key={entry.key}
            type="button"
            variant="tertiary"
            onClick={() => choose(index === 0 ? 0 : 1)}
            aria-pressed={shown === index}
            className={cn(
              "relative z-[1] h-8 min-w-[84px] rounded-full px-3 text-[13px] font-semibold hover:bg-transparent",
              shown === index ? "text-neutral-900" : "text-neutral-500",
            )}
          >
            {entry.label}
          </Button>
        ))}
      </div>

      <div className="relative mt-4 h-[300px] overflow-hidden rounded-xl border border-neutral-200 bg-white sm:h-[272px]">
        {sides.map((entry, index) => (
          <div
            key={entry.key}
            lang={entry.key}
            aria-hidden={shown !== index}
            className={cn(
              "absolute inset-0 p-5 transition-opacity duration-200 ease-out",
              shown === index ? "opacity-100" : "opacity-0",
            )}
          >
            <p className="text-[17px] font-bold text-neutral-900">{entry.side.title}</p>
            {entry.side.paragraphs.map((paragraph, paragraphIndex) => (
              <p key={paragraphIndex} className="mt-2.5 text-sm leading-relaxed text-neutral-600">
                {paragraph}
              </p>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
